import api from '@/lib/api';
import { getAll, putAll, putItem, addPendingSync, getPendingSync, clearPendingSync, STORES } from '@/lib/offlineDb';

let syncInProgress = false;
let onStatusChange = null;

export function setSyncStatusCallback(cb) {
  onStatusChange = cb;
}

function notifyStatus(status) {
  if (onStatusChange) onStatusChange(status);
}

export async function cacheClients(clients) {
  try {
    await putAll(STORES.clients, clients);
  } catch {}
}

export async function cacheCategories(categories) {
  try {
    await putAll(STORES.categories, categories);
  } catch {}
}

export async function cacheDashboardStats(stats) {
  try {
    await putItem(STORES.dashboardStats, { key: 'stats', ...stats });
  } catch {}
}

export async function getCachedClients() {
  try {
    return await getAll(STORES.clients);
  } catch {
    return [];
  }
}

export async function getCachedCategories() {
  try {
    return await getAll(STORES.categories);
  } catch {
    return [];
  }
}

export async function getCachedDashboardStats() {
  try {
    const { getItem } = await import('@/lib/offlineDb');
    return await getItem(STORES.dashboardStats, 'stats');
  } catch {
    return null;
  }
}

export async function queueOfflineOperation(method, url, data) {
  await addPendingSync({ method, url, data });
  notifyStatus('pending');
}

export async function syncPendingOperations() {
  if (syncInProgress || !navigator.onLine) return;
  const pending = await getPendingSync();
  if (pending.length === 0) return;

  syncInProgress = true;
  notifyStatus('syncing');

  let successCount = 0;
  for (const op of pending) {
    try {
      if (op.method === 'POST') {
        await api.post(op.url, op.data);
      } else if (op.method === 'PUT') {
        await api.put(op.url, op.data);
      } else if (op.method === 'DELETE') {
        await api.delete(op.url);
      }
      successCount++;
    } catch (e) {
      console.error('Sync failed for operation:', op, e);
    }
  }

  if (successCount === pending.length) {
    await clearPendingSync();
    notifyStatus('online');
  } else {
    notifyStatus('error');
  }
  syncInProgress = false;
}

export function initOfflineSync() {
  window.addEventListener('online', () => {
    notifyStatus('online');
    syncPendingOperations();
  });

  window.addEventListener('offline', () => {
    notifyStatus('offline');
  });

  if (!navigator.onLine) {
    notifyStatus('offline');
  } else {
    notifyStatus('online');
    syncPendingOperations();
  }
}

export function getOnlineStatus() {
  return navigator.onLine ? 'online' : 'offline';
}
